import { useEffect, useState, type ReactNode } from "react";
import { Car, CircleAlert, ClipboardCheck, Eye, EyeOff, FileBadge, Globe, KeyRound } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { PrivacyPolicyDialog } from "./PrivacyPolicyDialog";

const REMEMBER_KEY = "meta.gestao-veicular.remembered-email";
const SLIDE_MS = 5500;

const slides: { icon: typeof Car; title: string; detail: string }[] = [
  { icon: ClipboardCheck, title: "Vistorias", detail: "Checklist de 39 itens com fotos e resultado calculado automaticamente." },
  { icon: KeyRound, title: "Credenciais", detail: "Aprovação e emissão de credenciais veiculares com rastreabilidade." },
  { icon: FileBadge, title: "Documentos", detail: "Validade de documentos de empresas, motoristas e veículos sob controle." },
  { icon: Car, title: "Frota", detail: "Veículos, motoristas e empresas reunidos em uma única fonte." }
];

const LinkedinIcon = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></svg>;
const InstagramIcon = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect width="20" height="20" x="2" y="2" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>;

const socialLinks: { label: string; href: string; icon: ReactNode }[] = [
  { label: "Site da Meta CS", href: "https://metacs.com.br/", icon: <Globe size={18} strokeWidth={1.75} aria-hidden="true"/> },
  { label: "LinkedIn da Meta CS", href: "https://www.linkedin.com/company/metacs/posts/?feedView=all", icon: <LinkedinIcon/> },
  { label: "Instagram da Meta CS", href: "https://www.instagram.com/meta.cds/", icon: <InstagramIcon/> }
];

function readRememberedEmail() {
  try { return localStorage.getItem(REMEMBER_KEY) || ""; } catch { return ""; }
}

function BrandPanel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const slide = slides[index];
  const Icon = slide.icon;

  useEffect(() => {
    if (paused) return;
    const id = window.setTimeout(() => setIndex((current) => (current + 1) % slides.length), SLIDE_MS);
    return () => window.clearTimeout(id);
  }, [index, paused]);

  return <aside
    className="cs-login-brand"
    aria-label="O que a plataforma faz"
    aria-roledescription="carrossel"
    onMouseEnter={() => setPaused(true)}
    onMouseLeave={() => setPaused(false)}
    onFocusCapture={() => setPaused(true)}
    onBlurCapture={() => setPaused(false)}
  >
    <div className="cs-login-brand-top">
      <img className="cs-login-brand-logo" src="/assets/logo-meta-branca.png" alt="Meta Central de Serviços"/>
      <img className="cs-login-brand-seal" src="/assets/30anosmeta.png" alt="Meta, 30 anos"/>
    </div>
    <div className="cs-login-brand-art" aria-hidden="true">
      <div key={slide.title} className="cs-login-brand-art-tile"><Icon strokeWidth={1.25}/></div>
    </div>
    <div className="cs-login-brand-copy" aria-live={paused ? "polite" : "off"}>
      <div key={slide.title} className="cs-login-brand-slide">
        <h2>{slide.title}</h2>
        <p>{slide.detail}</p>
      </div>
    </div>
    <div className="cs-login-brand-nav" role="group" aria-label="Escolher módulo">
      {slides.map((item, i) => {
        const active = i === index;
        return <button key={item.title} type="button" onClick={() => setIndex(i)} aria-label={`Mostrar ${item.title}`} aria-current={active ? "true" : undefined}>
          <span className="cs-login-brand-track">
            {i < index && <span className="cs-login-brand-fill done"/>}
            {active && <span key={`${index}-${paused}`} className={`cs-login-brand-fill ${paused ? "done" : "running"}`} style={{ animationDuration: `${SLIDE_MS}ms` }}/>}
          </span>
        </button>;
      })}
    </div>
    <div className="cs-login-brand-footer">
      <strong>Meta Gestão Veicular</strong>
      <span>Meta Central de Serviços</span>
    </div>
  </aside>;
}

export function Login() {
  const { signIn, resetPassword, error } = useAuth();
  const [email, setEmail] = useState(readRememberedEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => !!readRememberedEmail());
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [feedback, setFeedback] = useState("");

  const handleSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!acceptedTerms) return;
    setRedirecting(true);
    setFeedback("");
    try {
      try { if (remember) localStorage.setItem(REMEMBER_KEY, email); else localStorage.removeItem(REMEMBER_KEY); } catch { /* armazenamento indisponível */ }
      await signIn(email, password);
    } catch { setRedirecting(false); }
  };

  const handleReset = async () => {
    setFeedback("");
    try { await resetPassword(email); setFeedback("Se o e-mail estiver cadastrado, você receberá as instruções de redefinição."); }
    catch (reason) { setFeedback(reason instanceof Error ? reason.message : "Não foi possível solicitar a redefinição."); }
  };

  return <main className="cs-login-shell">
    <div className="cs-login-frame">
      <BrandPanel/>
      <section className="cs-login-content" aria-labelledby="login-title">
        <p className="cs-login-content-mark"><strong>Meta Gestão Veicular</strong></p>
        <div className="cs-login-panel">
          <div className="cs-login-welcome">
            <h1 id="login-title">Acesse sua conta</h1>
            <p id="login-support">Entre com seu e-mail corporativo e senha da plataforma Meta CS.</p>
          </div>
          {error && <div className="cs-login-alert error" role="alert"><CircleAlert size={16} aria-hidden="true"/><div><strong>Não foi possível entrar.</strong><span>{error}</span></div></div>}
          {feedback && <div className="cs-login-alert info" role="status"><CircleAlert size={16} aria-hidden="true"/><div><span>{feedback}</span></div></div>}
          <form className="cs-login-form" onSubmit={handleSignIn} aria-describedby="login-support">
            <div className="cs-login-field">
              <label htmlFor="login-email">E-mail corporativo</label>
              <input id="login-email" type="email" autoComplete="username" inputMode="email" spellCheck={false} required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="nome@metacs.com.br" aria-invalid={error ? true : undefined}/>
            </div>
            <div className="cs-login-field">
              <div className="cs-login-field-row">
                <label htmlFor="login-password">Senha</label>
                <button type="button" className="cs-login-link" onClick={() => void handleReset()}>Esqueci minha senha</button>
              </div>
              <div className="cs-login-password">
                <input id="login-password" type={showPassword ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Sua senha" aria-invalid={error ? true : undefined}/>
                <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} aria-pressed={showPassword}>{showPassword ? <EyeOff size={16} aria-hidden="true"/> : <Eye size={16} aria-hidden="true"/>}</button>
              </div>
            </div>
            <div className="cs-login-checks">
              <label className="cs-login-check"><input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)}/><span>Lembrar meu e-mail neste dispositivo</span></label>
              <label className="cs-login-check"><input type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)}/><span>Li e aceito a <button type="button" className="cs-login-link underline" onClick={(event) => { event.preventDefault(); setPolicyOpen(true); }}>Política de Privacidade e Proteção de Dados (LGPD)</button></span></label>
            </div>
            <div>
              <button className="button primary wide cs-login-submit" type="submit" disabled={redirecting || !acceptedTerms} aria-describedby={!acceptedTerms ? "login-consent-hint" : undefined}>
                <span>{redirecting ? "Autenticando…" : "Entrar na plataforma"}</span>
                {redirecting && <span className="button-spinner" aria-hidden="true"/>}
              </button>
              {!acceptedTerms && <p id="login-consent-hint" className="cs-login-hint">Aceite a política de privacidade para liberar o acesso.</p>}
            </div>
          </form>
        </div>
        <div className="cs-login-legal">
          <p>&copy; {new Date().getFullYear()} Meta Central de Serviços. Todos os direitos reservados.</p>
          <nav aria-label="Meta CS nas redes">
            {socialLinks.map(({ label, href, icon }) => <a key={href} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label}>{icon}</a>)}
          </nav>
        </div>
      </section>
    </div>
    <PrivacyPolicyDialog open={policyOpen} onClose={() => setPolicyOpen(false)}/>
  </main>;
}
